import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';

import { FileUpload } from 'primereact/fileupload';
import { classNames } from 'primereact/utils';
import { Controller, get } from 'react-hook-form';
import { useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';

export default function FileInput({
  label,
  name,
  isRequired,
  hideRequiredAsterisk,
  disabled,
  multiple,
  accept,
  acceptFileMessage,
  control,
  errors,
  acceptOnly,
  innerRef,
  maxFileSize,
}) {
  const { t } = useTranslation();
  const toast = useRef(null);

  const uploadHandler = (event, onChange) => {
    if (multiple) {
      onChange(event.files);
    } else {
      const fileName = event.files[0]?.name;
      let fileExt = fileName?.split('.').pop();
      fileExt = fileExt === fileName ? '' : `.${fileExt}`;

      if (accept !== '*') {
        const acceptExts = accept?.split(',');
        if (!acceptExts.includes(fileExt.toLowerCase())) {
          toast.current.show({
            severity: 'error',
            summary: t('toast.error'),
            detail: acceptFileMessage,
            life: 4000,
          });

          return;
        }
      }

      onChange(event.files[0]);
    }
  };

  const handleRemoveFile = (fileToRemove, currentFiles, onChange) => {
    if (multiple) {
      const files = currentFiles.filter((file) => file !== fileToRemove);
      onChange(files);
    } else {
      onChange('');
    }
  };

  return (
    <div className="field">
      <label htmlFor={name}>
        {label}{' '}
        {isRequired && !hideRequiredAsterisk && (
          <span className="text-red-500" style={{ fontWeight: 900 }}>
            *
          </span>
        )}
      </label>
      <Controller
        control={control}
        name={name}
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <FileUpload
            ref={innerRef}
            name={name}
            multiple={multiple}
            accept={accept}
            maxFileSize={maxFileSize}
            emptyTemplate={
              // eslint-disable-next-line react/jsx-wrap-multilines
              <p className="m-0">
                {t('formControl.fileInput')}{' '}
                <span className="text-red-500">
                  {acceptOnly ? `(${t(`formControl.${acceptOnly}`)})` : ''}
                </span>
              </p>
            }
            chooseLabel={t('formControl.fileBrowse')}
            customUpload
            uploadHandler={(event) => {
              uploadHandler(event, onChange);
            }}
            className={classNames(
              { 'p-invalid': !!get(errors, name), 'surface-200': disabled },
              'custom-tooltip-btn'
            )}
            disabled={disabled}
            auto
            onRemove={(event) => {
              handleRemoveFile(event.file, value, onChange);
            }}
          />
        )}
      />
      <Tooltip
        target=".custom-tooltip-btn"
        content={t(get(errors, name)?.message)}
        position="bottom"
      />

      <Toast ref={toast} />
    </div>
  );
}

FileInput.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  isRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  accept: PropTypes.string,
  acceptFileMessage: PropTypes.string,
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  acceptOnly: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  hideRequiredAsterisk: PropTypes.bool,
  maxFileSize: PropTypes.number,
};

FileInput.defaultProps = {
  label: '',
  name: '',
  isRequired: false,
  disabled: false,
  multiple: false,
  accept: '*',
  acceptFileMessage: '',
  acceptOnly: '',
  innerRef: null,
  hideRequiredAsterisk: false,
  maxFileSize: 10000000,
};
